import type { CommerceOption } from '@/hooks/useSuperStats';

interface StatsFilterHeroProps {
  commerces: CommerceOption[];
  scopeId: number;
  startDate: string;
  endDate: string;
  loading: boolean;
  onScopeChange: (value: number) => void;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  onQuickRange: (days: number) => void;
  activeQuickDays: number | null;
  onApply: () => void;
  onExport: () => void;
  onExportPdf: () => void;
  onExportPptx: () => void;
  onOpenDocuments: () => void;
  savedDocName?: string | null;
  exportDisabled: boolean;
  exportingPptx?: boolean;
}

function formatDateFr(value: string): string {
  if (!value) return '—';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Périodes rapides : change tout le périmètre d'un clic. */
const QUICK_RANGES: { label: string; days: number }[] = [
  { label: '7 jours', days: 7 },
  { label: '30 jours', days: 30 },
  { label: '90 jours', days: 90 },
  { label: 'Année', days: 365 },
];

/**
 * En-tête du Super Dashboard : présentation + filtre « commerce / site » et
 * plage de dates.
 */
export default function StatsFilterHero({
  commerces,
  scopeId,
  startDate,
  endDate,
  loading,
  onScopeChange,
  onStartChange,
  onEndChange,
  onQuickRange,
  activeQuickDays,
  onApply,
  onExport,
  onExportPdf,
  onExportPptx,
  onOpenDocuments,
  savedDocName = null,
  exportDisabled,
  exportingPptx = false,
}: StatsFilterHeroProps) {
  return (
    <section className="rounded-lg border border-background-200/70 bg-gradient-to-br from-secondary-900 via-secondary-800 to-primary-700 text-background-50 p-5 md:p-8 mb-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center">
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50/15 text-xs font-medium text-background-50 mb-4">
            <i className="ri-database-2-line"></i>
            Données réelles Zifek
          </span>
          <h2 className="text-2xl md:text-3xl font-bold font-heading mb-3 text-background-50">
            Super Dashboard SaaS
          </h2>
          <p className="text-sm text-background-50/80 leading-relaxed max-w-md">
            Sites hébergés, produits, services, utilisateurs, sources et
            géolocalisation des visiteurs — vue d&apos;ensemble de la plateforme.
          </p>
        </div>

        <div className="rounded-lg bg-background-50/10 border border-background-50/20 p-4 md:p-5 backdrop-blur-sm">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-xs font-medium text-background-50/80 mr-1 whitespace-nowrap">
              Période rapide :
            </span>
            {QUICK_RANGES.map((qr) => {
              const isActive = activeQuickDays === qr.days;
              return (
                <button
                  key={qr.days}
                  type="button"
                  disabled={loading}
                  onClick={() => onQuickRange(qr.days)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 ${
                    isActive
                      ? 'bg-background-50 text-foreground-950'
                      : 'bg-background-50/15 text-background-50 hover:bg-background-50/25'
                  }`}
                >
                  {qr.label}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-3">
              <label className="block text-xs font-medium text-background-50/80 mb-1.5">
                Site
              </label>
              <div className="relative">
                <select
                  value={scopeId}
                  onChange={(e) => onScopeChange(Number(e.target.value))}
                  disabled={loading}
                  className="w-full appearance-none bg-background-50 text-foreground-950 text-sm rounded-md px-3 py-2.5 pr-9 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <option value={0}>Tout le SaaS</option>
                  {commerces.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.typeName ? ` — ${c.typeName}` : ''}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none"></i>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-background-50/80 mb-1.5">
                Début
              </label>
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(e) => onStartChange(e.target.value)}
                className="w-full bg-background-50 text-foreground-950 text-sm rounded-md px-3 py-2.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-background-50/80 mb-1.5">
                Fin
              </label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => onEndChange(e.target.value)}
                className="w-full bg-background-50 text-foreground-950 text-sm rounded-md px-3 py-2.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={onApply}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-background-50 text-foreground-950 text-sm font-semibold rounded-md px-4 py-2.5 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {loading ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-filter-3-line"></i>
                )}
                Appliquer le filtre
              </button>
            </div>
          </div>
          <p className="text-[11px] text-background-50/70 mt-3">
            Période analysée : <strong>{formatDateFr(startDate)}</strong> → <strong>{formatDateFr(endDate)}</strong>
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button
              type="button"
              onClick={onExportPdf}
              disabled={exportDisabled}
              className="inline-flex items-center gap-1.5 bg-background-50 text-foreground-950 text-xs font-semibold rounded-md px-3 py-2 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <i className="ri-file-pdf-2-line"></i>
              Rapport investisseurs (PDF)
            </button>
            <button
              type="button"
              onClick={onExportPptx}
              disabled={exportDisabled || exportingPptx}
              className="inline-flex items-center gap-1.5 bg-accent-500 text-background-50 text-xs font-semibold rounded-md px-3 py-2 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <i className={exportingPptx ? 'ri-loader-4-line animate-spin' : 'ri-slideshow-3-line'}></i>
              {exportingPptx ? 'Génération…' : 'Présentation (Documents)'}
            </button>
            {savedDocName && (
              <button
                type="button"
                onClick={onOpenDocuments}
                className="inline-flex items-center gap-1.5 bg-background-50 text-foreground-950 text-xs font-semibold rounded-md px-3 py-2 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-folder-open-line"></i>
                Ouvrir dans Documents
              </button>
            )}
            <button
              type="button"
              onClick={onExport}
              disabled={exportDisabled}
              className="inline-flex items-center gap-1.5 bg-background-50/10 border border-background-50/30 text-background-50 text-xs font-medium rounded-md px-3 py-2 hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <i className="ri-download-2-line"></i>
              Exporter CSV
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}